import { useEffect, useRef } from 'react';
import { Marker } from 'maplibre-gl';
import { useMapStore } from '@/stores/mapStore';
import { useSharedLocationStore } from './sharedLocationStore';

/**
 * Drops a distinct marker at the shared-location pin (arrives via a `?ll=` link
 * or the Share flow). Follows the imperative Marker pattern in
 * components/map/MapMarkers — the marker is a DOM element added straight to the
 * MapLibre map, kept in a ref, and cleaned up on unmount / when the pin clears.
 */
export function SharedLocationMarker() {
  const map = useMapStore((s) => s.map);
  const isLoaded = useMapStore((s) => s.isLoaded);
  const pin = useSharedLocationStore((s) => s.pin);
  const markerRef = useRef<Marker | null>(null);

  useEffect(() => {
    if (!map || !isLoaded) return;

    if (pin) {
      if (!markerRef.current) {
        markerRef.current = new Marker({ element: createSharedMarkerElement(), anchor: 'bottom' })
          .setLngLat([pin.lng, pin.lat])
          .addTo(map);
      } else {
        markerRef.current.setLngLat([pin.lng, pin.lat]);
      }
    } else if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }

    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    };
  }, [map, isLoaded, pin]);

  return null;
}

function createSharedMarkerElement(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'shared-location-marker';
  // Purple teardrop pin with a soft ping halo — visually distinct from the blue
  // search markers and red route destination, so a shared spot reads as special.
  el.innerHTML = `
    <div class="relative">
      <span class="absolute left-1/2 top-9 h-3 w-3 -translate-x-1/2 rounded-full bg-purple-500/30 animate-ping"></span>
      <svg width="32" height="40" viewBox="0 0 32 40" fill="none">
        <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 24 16 24s16-12 16-24C32 7.163 24.837 0 16 0z" fill="#7c3aed"/>
        <circle cx="16" cy="16" r="6" fill="white"/>
      </svg>
    </div>
  `;
  return el;
}
